import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from '@tanstack/react-query';

import {
  listThreadMessages,
  type PagedMessages,
} from '@/features/chat/chat.api';
import { chatKeys } from '@/features/chat/keys';

/**
 * Page size for the merged-thread history — matches the backend endpoint's
 * own default (`limit` Query default 30 on
 * `GET /api/v1/threads/{thread_key}/messages`) and the per-conversation
 * hook's lean-first-paint window (`useMessages`).
 */
const PAGE_SIZE = 30;

/**
 * Infinite list of messages for one merged phone-thread — the union of
 * every member conversation's messages, interleaved by the backend
 * (`created_at DESC, id DESC`).
 *
 * Mirrors `useMessages` exactly:
 *   - newest message is `pages[0].items[0]`; `MessageList` flattens +
 *     reverses for oldest-at-top display.
 *   - `getNextPageParam` walks the accumulated count against `total`.
 *   - `staleTime`/`refetchInterval` 30s — the WS `message.new` projection
 *     patches this cache in place for open-thread members (see
 *     `projection.ts`); polling is the safety net, same as the
 *     per-conversation window.
 *   - `enabled` — only fire once a thread is selected.
 *
 * The cache slot (`chatKeys.threadMessages(key)`) uses the SAME
 * `InfiniteMessages` envelope as the per-conversation slot, so
 * `mergeServerMessage` / `patchCacheWith` (the one true merge path) apply
 * verbatim — no second merge implementation.
 */
export function useThreadMessages(
  threadKey: string | null,
): UseInfiniteQueryResult<{ pages: PagedMessages[]; pageParams: number[] }, Error> {
  return useInfiniteQuery<
    PagedMessages,
    Error,
    { pages: PagedMessages[]; pageParams: number[] },
    ReturnType<typeof chatKeys.threadMessages>,
    number
  >({
    // Stable empty-string key when nothing is selected; `enabled` guards the
    // fetch so the value is never read (same pattern as `useMessages`).
    queryKey: chatKeys.threadMessages(threadKey ?? ''),
    queryFn: ({ pageParam }) =>
      listThreadMessages({
        threadKey: threadKey as string,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const consumed = allPages.reduce((sum, p) => sum + p.items.length, 0);
      if (consumed >= lastPage.total) {
        return undefined;
      }
      return consumed;
    },
    enabled: !!threadKey,
    staleTime: 30_000,
    // Keep an inactive thread's window cached well past react-query's
    // 5-minute default — operators hop between customers constantly, and a
    // GC'd cache made the history "disappear" until a full reload (the WS
    // projection would rebuild the slot with only the newest trickle).
    gcTime: 30 * 60_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}
